/**
 * MobileEmulatorPage - Refactored with hooks and sub-components
 *
 * Original: 978 lines
 * Refactored: ~150 lines
 *
 * Extracted:
 * - 2 custom hooks (useMobileData, useMobileScanner)
 * - 6 sub-components (MobileStatusBar, SyncScreen, HospitalScreen, LocalScreen, ScannerModal, CountingScreen)
 */
import { useState } from 'react';
import { msg } from '../utils/messages';
import mobileService from '../services/mobileService';
import DebugPanel from '../components/DebugPanel';

// Custom hooks
import { useMobileData, useMobileScanner } from '../hooks';

// Sub-components
import {
  MobileStatusBar,
  SyncScreen,
  HospitalScreen,
  LocalScreen,
  CountingScreen,
} from '../components/mobile';

export const MobileEmulatorPage = () => {
  // Debug panel state
  const [debugPanelVisible, setDebugPanelVisible] = useState(false);

  // Data hook
  const {
    isOnline,
    setIsOnline,
    currentScreen,
    setCurrentScreen,
    hospitais,
    locais,
    selectedHospital,
    setSelectedHospital,
    selectedLocal,
    setSelectedLocal,
    currentJobId,
    setCurrentJobId,
    isSyncing,
    loadingHospitais,
    loadingLocais,
    error: dataError,
    setError: setDataError,
    handleSync,
    handleSelectHospital,
    handleSelectLocal,
  } = useMobileData();

  // Scanner hook
  const {
    scannerAtivo,
    setScannerAtivo,
    codigoBarras,
    setCodigoBarras,
    ultimaLeitura,
    feedbackLeitura,
    codigoBarrasInputRef,
    error: scannerError,
    setError: setScannerError,
    itensEscaneados,
    itensContados,
    setItensContados,
    handleSimularLeitura,
    handleBuscarProdutoPorCodigo,
    handleEditarQuantidadeEscaneado,
    handleRemoverItemEscaneado,
    handleRemoverItem,
    handleConfirmarItensEscaneados,
    closeScanner,
  } = useMobileScanner();

  // Handler: Finalizar contagem
  const handleFinalizar = async () => {
    if (!isOnline || itensContados.length === 0 || !currentJobId) return;

    try {
      const counts = itensContados.map(item => ({
        produto_codigo: item.codigo,
        quantidade: item.quantidade,
        observacoes: `Contado em ${item.timestamp}`,
      }));

      await mobileService.uploadCounts(currentJobId, counts);
      await mobileService.finishJob(currentJobId);

      msg.success(`Contagem finalizada! ${itensContados.length} itens enviados.`);

      setItensContados([]);
      setCurrentScreen('hospital');
      setSelectedHospital(null);
      setSelectedLocal(null);
      setCurrentJobId(null);
    } catch (err: any) {
      console.error('Erro ao finalizar contagem:', err);
      setDataError(err.message || 'Erro ao finalizar contagem');
      msg.error(`Erro ao finalizar: ${err.message || 'Erro desconhecido'}`);
    }
  };

  // Render screen based on current state
  const renderScreen = () => {
    switch (currentScreen) {
      case 'sync':
        return (
          <SyncScreen
            isOnline={isOnline}
            isSyncing={isSyncing}
            onSync={handleSync}
            onOpenDebug={() => setDebugPanelVisible(true)}
          />
        );
      case 'hospital':
        return (
          <HospitalScreen
            hospitais={hospitais}
            loadingHospitais={loadingHospitais}
            loadingLocais={loadingLocais}
            error={dataError}
            onSelectHospital={handleSelectHospital}
          />
        );
      case 'local':
        return (
          <LocalScreen
            selectedHospital={selectedHospital}
            locais={locais}
            loadingLocais={loadingLocais}
            error={dataError}
            onBack={() => setCurrentScreen('hospital')}
            onSelectLocal={handleSelectLocal}
          />
        );
      case 'counting':
        return (
          <CountingScreen
            selectedHospital={selectedHospital}
            selectedLocal={selectedLocal}
            isOnline={isOnline}
            isSyncing={isSyncing}
            itensContados={itensContados}
            scannerAtivo={scannerAtivo}
            codigoBarras={codigoBarras}
            ultimaLeitura={ultimaLeitura}
            feedbackLeitura={feedbackLeitura}
            itensEscaneados={itensEscaneados}
            scannerError={scannerError}
            codigoBarrasInputRef={codigoBarrasInputRef}
            onBack={() => setCurrentScreen('local')}
            onOpenScanner={() => setScannerAtivo(true)}
            onCloseScanner={closeScanner}
            onFinalizar={handleFinalizar}
            onLimparItens={() => setItensContados([])}
            onRemoverItem={handleRemoverItem}
            onCodigoBarrasChange={setCodigoBarras}
            onBuscarProduto={handleBuscarProdutoPorCodigo}
            onSimularLeitura={handleSimularLeitura}
            onEditarQuantidadeEscaneado={handleEditarQuantidadeEscaneado}
            onRemoverItemEscaneado={handleRemoverItemEscaneado}
            onConfirmarItensEscaneados={handleConfirmarItensEscaneados}
          />
        );
      default:
        return (
          <SyncScreen
            isOnline={isOnline}
            isSyncing={isSyncing}
            onSync={handleSync}
            onOpenDebug={() => setDebugPanelVisible(true)}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* Smartphone Frame (380x780px) */}
      <div
        className="relative bg-gray-900 rounded-[50px] shadow-2xl overflow-hidden"
        style={{ width: '380px', height: '780px' }}
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-gray-900 rounded-b-3xl z-20" />

        {/* Status Bar */}
        <MobileStatusBar
          isOnline={isOnline}
          onToggleOnline={() => setIsOnline(!isOnline)}
          onOpenDebug={() => setDebugPanelVisible(true)}
        />

        {/* Screen Content */}
        <div className="absolute top-16 left-0 right-0 bottom-0 bg-gray-50 overflow-hidden flex flex-col">
          {renderScreen()}
        </div>
      </div>

      {/* Debug Panel */}
      <DebugPanel
        visible={debugPanelVisible}
        onClose={() => setDebugPanelVisible(false)}
      />
    </div>
  );
};
